
import React, { useMemo, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUnifiedAuth } from '@/contexts/auth/UnifiedAuthProvider';
import { AuthSecurityValidator } from '@/lib/auth-security';
import { useSessionTimeout } from '@/hooks/use-session-timeout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertTriangle, Loader2 } from 'lucide-react';
import { secureFetch } from '@/lib/api-client';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { useQuery } from '@tanstack/react-query';
import { getVerifyAccessCached, setVerifyAccessCached } from '@/lib/verify-access-cache';
import { addBreadcrumb } from '@/lib/secureBreadcrumbs';
import { ensureFreshAuth } from '@/lib/auth-readiness';
import { useProgressiveEnable } from '@/hooks/use-progressive-enable';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredFeature?: string;
  requiredRole?: string;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredFeature,
  requiredRole,
  fallbackPath = '/auth'
}) => {
  const { user, loading: authLoading, subscriptionTier, authSettled, hardExpired } = useUnifiedAuth();
  const location = useLocation();

  // Initialize session timeout for protected routes
  useSessionTimeout({
    timeoutMinutes: 120,
    warningMinutes: 10,
    onTimeout: () => {
      logger.warn('Session timeout in protected route');
    }
  });

  const requiredSubscription = useMemo(() => {
    if (!requiredFeature) return 'free';
    switch (requiredFeature) {
      case 'mentor_feedback':
      case 'coach_progress':
      case 'technique_analysis':
        return 'premium';
      default:
        return 'free';
    }
  }, [requiredFeature]);

  const userId = user?.id || 'anon';
  const { enable } = useProgressiveEnable(user || undefined ? userId : undefined);
  const { data: verificationData, isLoading: verificationLoading, error: verificationErr, refetch } = useQuery({
    queryKey: ['verify-access', user?.id || 'anon', requiredFeature || 'none'],
    enabled: !!requiredFeature && enable,
    refetchOnMount: 'always',
    queryFn: async (): Promise<{ access: boolean }> => {
      addBreadcrumb('guard', 'verify-start', { feature: requiredFeature });
      // Grace cache: short-lived UX cache to avoid re-verifying on immediate refocus
      const cached = getVerifyAccessCached(user!.id, requiredFeature || 'none');
      if (cached) {
        addBreadcrumb('guard', 'verify-cache-hit', { feature: requiredFeature, access: cached.access });
        logger.debug('verify-access (grace cache hit)', { userId: cached.userId, feature: cached.feature, access: cached.access });
        return { access: cached.access };
      }

      const timeout = new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Verification timeout')), 10_000));
      const respPromise = secureFetch('verify-access', {
        method: 'POST',
        body: JSON.stringify({ requiredSubscription })
      });
      const response = await Promise.race([respPromise, timeout]) as Response;
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Server verification failed' }));
        addBreadcrumb('guard', 'verify-error', { status: response.status });
        logger.error('Server verification error', { status: response.status, errorData });
        throw new Error(errorData.error || 'Server verification failed');
      }
      const raw = await response.json();
      const { verifyAccessResponseSchema } = await import('@/types/dto');
      const parsed = verifyAccessResponseSchema.safeParse(raw);
      if (!parsed.success) {
        addBreadcrumb('guard', 'verify-empty');
        throw new Error('Invalid response from server verification');
      }
      const data = parsed.data;
      setVerifyAccessCached(user!.id, requiredFeature || 'none', !!data.access);
      addBreadcrumb('guard', 'verify-success', { access: !!data.access });
      logger.info('ProtectedRoute server verification result', {
        feature: requiredFeature,
        requiredSubscription,
        serverAccess: data.access,
        clientTier: subscriptionTier
      });
      return data;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  // Slow verification fallback after 10s if still loading
  const [slowVerify, setSlowVerify] = React.useState(false);
  React.useEffect(() => {
    if (authLoading) return;
    if (!verificationLoading) {
      setSlowVerify(false);
      return;
    }
    const t = setTimeout(() => setSlowVerify(true), 10_000);
    return () => clearTimeout(t);
  }, [authLoading, verificationLoading]);

  // Non-blocking verification: spinner briefly, then allow optimistic render
  const [showOptimistic, setShowOptimistic] = useState(false);
  useEffect(() => {
    if (verificationLoading) {
      const t = setTimeout(() => setShowOptimistic(true), 1000);
      return () => clearTimeout(t);
    } else {
      setShowOptimistic(false);
    }
  }, [verificationLoading]);

  // Show loading while auth is loading, or while verify is loading for the first second
  if (authLoading || (verificationLoading && !showOptimistic)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{slowVerify ? 'Verification is taking longer than expected. You can retry.' : 'Verifying access...'}</p>
          {slowVerify && (
            <div className="mt-4">
              <button
                className="inline-flex items-center rounded-md border px-3 py-2 text-sm"
                onClick={() => refetch()}
              >
                Retry Verification
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuth && !user) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Validate user session if authenticated
  if (user && !AuthSecurityValidator.validateUserId(user.id)) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Invalid session detected. Please sign in again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Server authority: redirect to subscription page if access is denied
  if (requiredFeature && user && verificationData && verificationData.access === false) {
    return <Navigate to="/subscription" state={{ requiredFeature }} replace />;
  }

  // Show error state if server verification failed
  if (requiredFeature && verificationErr) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Unable to verify your access. You can retry the verification.
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <button
            className="inline-flex items-center rounded-md border px-3 py-2 text-sm"
            onClick={() => refetch()}
          >
            Retry Verification
          </button>
        </div>
      </div>
    );
  }

  // Role-based access control (placeholder for future implementation)
  if (requiredRole && user) {
    // This would check user roles from a roles table
    // Role checking logic would go here
    // For now, we'll just allow access
  }

  return <>{children}</>;
};

export default ProtectedRoute;
